import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {Button} from '@/components/ui/button'
import type {components} from '@/lib/api-schema'

export type FotoFinding = components['schemas']['FotoFindingRead']

type FotoAnalysis = {
  analysis_status: components['schemas']['ValidationStatus']
  analysis_findings: FotoFinding[] | null
  analysis_dismissed_at: string | null
}

// Un código sin copy propio cae al `detail_es` del modelo: nunca sale en blanco.
function findingCopy(t: TFunction, finding: FotoFinding, hint: string | null): string {
  if (finding.code === 'no_corresponde')
    return hint ? t('fotoAnalysis.finding.no_corresponde', {hint}) : t('fotoAnalysis.finding.no_corresponde_generico')
  if (finding.code === 'ilegible') return t('fotoAnalysis.finding.ilegible')
  return finding.detail_es ?? t('fotoAnalysis.finding.generic')
}

/** Solo consultivo y solo en superficies del operador (vision-fotos-spec §7): el empleado no lo ve. */
export function FotoAnalysisChip({
  foto,
  hint,
  editable,
  onDismiss,
  onRetry,
}: {
  foto: FotoAnalysis
  hint: string | null
  editable: boolean
  onDismiss: () => void
  onRetry: () => void
}) {
  const {t} = useTranslation()
  const dismissed = foto.analysis_dismissed_at != null

  if (foto.analysis_status === 'pending')
    return (
      <p className="font-mono text-[9px] tracking-wider text-muted-foreground uppercase" data-testid="foto-ai-pending">
        {t('fotoAnalysis.pending')}
      </p>
    )

  if (foto.analysis_status === 'ok')
    return (
      <p className="font-mono text-[9px] tracking-wider text-primary" data-testid="foto-ai-ok">
        {t('fotoAnalysis.ok')}
      </p>
    )

  if (foto.analysis_status === 'error')
    return (
      <div className="flex items-center gap-2" data-testid="foto-ai-error">
        <span className="font-mono text-[9px] tracking-wider text-muted-foreground uppercase">
          {t('fotoAnalysis.error')}
        </span>
        {editable && (
          <button type="button" className="text-[10px] font-medium text-primary hover:underline" onClick={onRetry}>
            {t('fotoAnalysis.retry')}
          </button>
        )}
      </div>
    )

  if (foto.analysis_status === 'flagged' && !dismissed)
    return (
      <div className="tone-amber space-y-1 rounded px-1.5 py-1 text-[10px]" data-testid="foto-ai-flagged">
        {(foto.analysis_findings ?? []).map((finding) => (
          <p key={finding.code}>{findingCopy(t, finding, hint)}</p>
        ))}
        {editable && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[10px]"
            data-testid="foto-ai-dismiss"
            onClick={onDismiss}>
            {t('fotoAnalysis.dismiss')}
          </Button>
        )}
      </div>
    )

  // not_requested, skipped o descartado: sin chip.
  return null
}
